import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Mutarea agentilor pe alta adresa de API - comanda `MVA` din `it_comenzi`.
 * Numai pentru administratorii de platforma: pentru ceilalti capetele raspund
 * 403, iar ecranele nu deseneaza nimic.
 *
 *   GET  /settings/download_agent/api_server        tinta, feluri, ultimele comenzi
 *   POST /settings/download_agent/api_server/move   in masa, pe fel (kind, limit)
 *   GET  /devices/{id}/api-server                   tinta si ultima mutare a aparatului
 *   POST /devices/{id}/api-server/move              un aparat
 *   POST /settings/download_agent/api_server/moves/{id}/dismiss   scoate un rand din lista
 *   POST /settings/download_agent/api_server/dismiss_all          toate (status=ok: numai reusitele)
 *
 * Dismiss doar ascunde randul din lista; istoricul ramane pe server. O mutare
 * noua e un rand nou, deci statia apare din nou.
 *
 * Tinta vine de la server: e adresa din comenzile de instalare (`API_SERVER`,
 * altfel `https://api.optimumdesk.ai`), nu se scrie aici.
 */

export type AgentKind = 'windows' | 'winserver' | 'linux' | 'mac'

export const AGENT_KINDS: { kind: AgentKind; label: string; icon: string }[] = [
  { kind: 'windows', label: 'Windows 10/11', icon: 'fab fa-windows' },
  { kind: 'winserver', label: 'Windows Server', icon: 'fas fa-server' },
  { kind: 'linux', label: 'Linux', icon: 'fab fa-linux' },
  { kind: 'mac', label: 'macOS', icon: 'fab fa-apple' },
]

export type MoveState = 'pending' | 'sent' | 'ok' | 'error' | 'expired'

export type AgentMove = {
  id: number
  deviceId: number
  deviceName: string
  url: string
  state: MoveState
  message: string
  created: string
  answered: string
}

export type ApiServerOverview = {
  target: string
  /** De mutat: agentii Go care NU sunt deja pe gazda tintei. */
  counts: Record<AgentKind, number>
  /** Deja pe gazda tintei - nu se mai muta. */
  onTarget?: Record<AgentKind, number>
  /** Agentii clasici 7.x: nu stiu comanda, nu se muta. */
  classic: number
  inFlight: number
  moves: AgentMove[]
}

/**
 * Unde vorbeste aparatul acum. `token` = gazda pe care a cerut ultimul token
 * (ph4.api o noteaza in `it_device_api_host`); `move` = ultima mutare cu
 * raspuns OK; `unknown` = nu se stie (`host` gol).
 */
export type ApiLocation = {
  host: string
  seenAt: string
  source: 'token' | 'move' | 'unknown'
}

export type DeviceApiServer = {
  target: string
  kind: AgentKind | null
  last: AgentMove | null
  current?: ApiLocation
  /** Deja pe gazda tintei: nu mai are ce muta, butonul nu se arata. */
  onTarget?: boolean
}

export type MoveAnswer =
  | { ok: true; queued: number; skipped: number; already?: number; target: string }
  | { ok: false; error: string }

export const apiServerOverviewQuery = queryOptions({
  queryKey: ['settings', 'download-agent', 'api-server'],
  queryFn: () => apiGet<ApiServerOverview>('/settings/download_agent/api_server'),
  retry: false,
})

export function deviceApiServerQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'api-server'],
    queryFn: () => apiGet<DeviceApiServer>(`/devices/${id}/api-server`),
    retry: false,
  })
}

export function moveFleet(kind: AgentKind | 'all', limit: number) {
  return apiPost<MoveAnswer>('/settings/download_agent/api_server/move', { kind, limit })
}

export function moveDevice(id: number) {
  return apiPost<MoveAnswer>(`/devices/${id}/api-server/move`, {})
}

export type DismissAnswer = { ok: true; dismissed: number } | { ok: false; error: string }

export function dismissMove(id: number) {
  return apiPost<DismissAnswer>(`/settings/download_agent/api_server/moves/${id}/dismiss`, {})
}

export function dismissAllMoves(onlyOk: boolean) {
  return apiPost<DismissAnswer>('/settings/download_agent/api_server/dismiss_all', onlyOk ? { status: 'ok' } : {})
}

export const MOVE_STATE_LABEL: Record<MoveState, string> = {
  pending: 'Waiting',
  sent: 'Sent, no answer yet',
  ok: 'OK',
  error: 'ERROR',
  expired: 'Expired',
}

/** Culoarea etichetei de stare, din clasele bootstrap ale portalului. */
export const MOVE_STATE_CLASS: Record<MoveState, string> = {
  pending: 'label label-warning',
  sent: 'label label-info',
  ok: 'label label-success',
  error: 'label label-danger',
  expired: 'label label-default',
}

/** Doar gazda, fara `https://` - ce se citeste pe buton. */
export function hostOf(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
}
